"use client";

import { Suspense } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import Link from "@/components/ui/Link";

type Props = {
  currentPage: number;
  totalPages: number;
  query?: string;
  onPageChange?: (page: number) => void;
  /** When false, pagination clicks won't scroll to top (e.g. photo carousel) */
  scrollToTop?: boolean;
};

function PaginationContent({ currentPage, totalPages, query, onPageChange, scrollToTop = true }: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const getPageUrl = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', page.toString());
    const basePath = pathname.replace(/\/[^/]*$/, '');
    const targetPath = query ? `${basePath}/${encodeURIComponent(query)}` : pathname;
    const url = `${targetPath}?${params.toString()}`;
    return url;
  };

  const handlePageClick = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Show max 5 page numbers with current page in center
  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range: number[] = [];
    const rangeWithDots: (number | string)[] = [];
    let l: number | undefined;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  // If onPageChange is provided, use buttons with callback (for client-side pagination)
  if (onPageChange) {
    return (
      <div className="flex justify-center items-center gap-1 sm:gap-2 py-8 border-[0.1px] border-E8E8E8 overflow-x-auto">
        {/* Previous Label */}
        <button
          onClick={() => { onPageChange(currentPage - 1); if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          disabled={currentPage === 1}
          className={`hidden sm:block text-14-english-400 line-clamp-1 leading-[1.3] ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-020202 hover:text-012061'}`}
        >
          Previous
        </button>

        {/* Left Arrow */}
        <button
          onClick={() => { onPageChange(currentPage - 1); if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          disabled={currentPage === 1}
          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center ${currentPage === 1 ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-222222 bg-ECEEF3 hover:bg-[#012061] hover:text-white'}`}
        >
          <ChevronLeft size={14} className="sm:size-[18px]" />
        </button>

        <div className="flex gap-1 sm:gap-2">
          {visiblePages.map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' ? (onPageChange(page), scrollToTop && window.scrollTo({ top: 0, behavior: 'smooth' })) : null}
              disabled={typeof page !== 'number'}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-12-english-400 sm:text-14-english-400 ${typeof page !== 'number' ? 'cursor-default text-gray-400 bg-transparent' : currentPage === page ? "bg-[#012061] text-FFFFFF cursor-pointer" : "bg-ECEEF3 text-020202 hover:bg-[#012061] hover:text-white cursor-pointer"}`}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => { onPageChange(currentPage + 1); if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          disabled={currentPage === totalPages}
          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center ${currentPage === totalPages ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-020202 bg-ECEEF3 hover:bg-[#012061] hover:text-white'}`}
        >
          <ChevronRight size={14} className="sm:size-[18px]" />
        </button>

        {/* Next Label */}
        <button
          onClick={() => { onPageChange(currentPage + 1); if (scrollToTop) window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          disabled={currentPage === totalPages}
          className={`hidden sm:block text-14-english-400 line-clamp-1 leading-[1.3] ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-020202 hover:text-012061'}`}
        >
          Next
        </button>
      </div>
    );
  }

  // Use Links for server-side pagination (proper page reload with new data)
  return (
    <div className="flex justify-center items-center gap-2 py-8 border-[0.1px] border-E8E8E8 overflow-x-auto">
      {/* Previous Label */}
      {currentPage === 1 ? (
        <span className="hidden sm:block text-14-english-400 line-clamp-1 leading-[1.3] text-gray-400 cursor-not-allowed">
          Previous
        </span>
      ) : (
        <Link          key="prev"
          href={getPageUrl(currentPage - 1)}
          onClick={() => handlePageClick(currentPage - 1)}
          className="hidden sm:block text-14-english-400 line-clamp-1 leading-[1.3] text-020202 hover:text-012061"
        >
          Previous
        </Link>
      )}

      {/* Left Arrow */}
      {currentPage === 1 ? (
        <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-gray-400 bg-gray-100 cursor-not-allowed">
          <ChevronLeft size={14} className="sm:size-[18px]" />
        </span>
      ) : (
        <Link
          href={getPageUrl(currentPage - 1)}
          onClick={() => handlePageClick(currentPage - 1)}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-222222 bg-ECEEF3 hover:bg-[#012061] hover:text-white"
        >
          <ChevronLeft size={14} className="sm:size-[18px]" />
        </Link>
      )}

      <div className="flex gap-1 sm:gap-2">
        {visiblePages.map((page, index) => {
          if (typeof page !== 'number') {
            return (
              <span
                key={index}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-12-english-400 sm:text-14-english-400 cursor-default text-gray-400 bg-transparent"
              >
                {page}
              </span>
            );
          }

          if (currentPage === page) {
            return (
              <span
                key={index}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-12-english-400 sm:text-14-english-400 bg-[#012061] text-FFFFFF"
              >
                {page}
              </span>
            );
          }

          return (
            <Link
              key={index}
              href={getPageUrl(page)}
              onClick={() => handlePageClick(page)}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-12-english-400 sm:text-14-english-400 bg-ECEEF3 text-020202 hover:bg-[#012061] hover:text-white cursor-pointer"
            >
              {page}
            </Link>
          );
        })}
      </div>

      {/* Right Arrow */}
      {currentPage === totalPages ? (
        <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-gray-400 bg-gray-100 cursor-not-allowed">
          <ChevronRight size={14} className="sm:size-[18px]" />
        </span>
      ) : (
        <Link
          href={getPageUrl(currentPage + 1)}
          onClick={() => handlePageClick(currentPage + 1)}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center text-020202 bg-ECEEF3 hover:bg-[#012061] hover:text-white"
        >
          <ChevronRight size={14} className="sm:size-[18px]" />
        </Link>
      )}

      {/* Next Label */}
      {currentPage === totalPages ? (
        <span className="hidden sm:block text-14-english-400 line-clamp-1 leading-[1.3] text-gray-400 cursor-not-allowed">
          Next
        </span>
      ) : (
        <Link
          href={getPageUrl(currentPage + 1)}
          onClick={() => handlePageClick(currentPage + 1)}
          className="hidden sm:block text-14-english-400 line-clamp-1 leading-[1.3] text-020202 hover:text-012061"
        >
          Next
        </Link>
      )}
    </div>
  );
}

export default function Pagination(props: Props) {
  return (
    <Suspense fallback={<div className="flex justify-center items-center gap-4 py-8"><span className="text-gray-400">Loading...</span></div>}>
      <PaginationContent {...props} />
    </Suspense>
  );
}
